import { useMutation } from "@apollo/client";
import { ObjectId } from "bson";
import { useCallback, useEffect, useState } from "react";

import { SAVE_REPORT_QUERY, SET_SELECTED_QUESTIONS } from "../../../pages/ReportAnalytics/hooks/useQuery";

import { getInterviewerNotesKey } from "../store/helpers";

import { useAskPerformYardStore } from "../AskPerformYardStore";

// Numeric valueTypes mirror the interviewer prompt's chart pre-flight + listAvailableColumns.
// Column types come from `columnMeta` — the authoritative valueType map the submit tool folds
// into the config from listAvailableColumns / the question loaders — so a free-text question or
// a person field is never mis-classified as numeric, and a custom numeric field is.
const NUMERIC_VALUE_TYPES = new Set(["numeric", "monetary", "salary", "rating-scale", "calculator", "goalProgress"]);

type ColumnMeta = Record<string, string>;

const isNumericColumn = (colId: string, columnMeta: ColumnMeta): boolean => NUMERIC_VALUE_TYPES.has(columnMeta[colId]);

const NUMERIC_SUMMARY_FUNCTIONS = ["maximum", "minimum", "average", "sum", "count"] as const;

const buildSummaryColumnMetadata = (selectedColumns: string[], columnMeta: ColumnMeta): Record<string, unknown> => {
	const result: Record<string, unknown> = {};
	for (const colId of selectedColumns) {
		const numeric = isNumericColumn(colId, columnMeta);
		result[colId] = {
			isColumnVisible: true,
			currentlySelectedSummaryFunction: numeric ? "average" : "count",
			availableSummaryFunctions: numeric ? [...NUMERIC_SUMMARY_FUNCTIONS] : ["count"],
		};
	}
	return result;
};

const buildSummarizeByMetadata = (
	summarizeBy: string[],
	columnMeta: ColumnMeta
): Record<string, { selection: string | null }> => {
	const result: Record<string, { selection: string | null }> = {};
	// Date groupings need a bucket or R&A groups by raw timestamp and the summarized grid
	// renders empty; default to month (R&A's own UI default). Other types take a null selection.
	for (const colId of summarizeBy) {
		result[colId] = columnMeta[colId] === "date" ? { selection: "month" } : { selection: null };
	}
	return result;
};

const buildColumnMetadata = (selectedColumns: string[]): Record<string, Record<string, unknown>> => {
	const result: Record<string, Record<string, unknown>> = {};
	for (const colId of selectedColumns) result[colId] = {};
	return result;
};

// R&A reads the chart metric from a different axis key per chart type (see the chart Options
// components under ReportAnalytics/.../ReportingCharts/*): Bar + Histogram read xAxis.column as
// the metric; Scatter needs both x and y numeric metrics. Column/Trendline keep the prior yAxis
// shape (not flagged broken). Without the right key the chart opens with no metric and renders empty.
const buildVisualizeOptions = (
	selectedChart: string | undefined,
	summarizeBy: string[],
	selectedColumns: string[],
	columnMeta: ColumnMeta
): Record<string, unknown> => {
	if (!selectedChart) return {};
	// Persisting selectedChart without usable axes opens the report on a broken/empty chart.
	// Whenever the prerequisites for a valid chart config aren't met, omit the chart entirely
	// so the report falls back to Table (the admin can still pick a chart in R&A after save).
	if (summarizeBy.length === 0 || selectedColumns.length === 0) return {};
	const numericCols = selectedColumns.filter((c) => isNumericColumn(c, columnMeta));
	if (numericCols.length === 0) return {};
	const dataSeries = { column: `summary_${summarizeBy[0]}`, rangeOption: "TOP_10" };
	const axis = (column: string) => ({ column, hasSuggestedMin: true, hasSuggestedMax: true });
	switch (selectedChart) {
		case "Scatter Chart":
			// Scatter needs two numeric metrics (x and y); with fewer, ship as Table.
			if (numericCols.length < 2) return {};
			return {
				selectedChart,
				[selectedChart]: { dataSeries, xAxis: axis(numericCols[0]), yAxis: axis(numericCols[1]) },
			};
		case "Bar Chart":
		case "Histogram Chart":
			return { selectedChart, [selectedChart]: { dataSeries, xAxis: axis(numericCols[0]) } };
		default:
			return { selectedChart, [selectedChart]: { dataSeries, yAxis: axis(numericCols[0]) } };
	}
};

// Fields the agent doesn't author get prototype defaults (revisit on edit-existing-report).
const buildSaveVariables = (reportConfig: Record<string, unknown>) => {
	const selectedChartRaw = reportConfig.selectedChart;
	const selectedChart = typeof selectedChartRaw === "string" ? selectedChartRaw : undefined;
	const authoredColumns = reportConfig.selectedColumns;
	const selectedColumns: string[] | null =
		Array.isArray(authoredColumns) && authoredColumns.length > 0 ? (authoredColumns as string[]) : null;
	const summarizeByRaw = reportConfig.summarizeBy;
	const summarizeBy: string[] = Array.isArray(summarizeByRaw) ? (summarizeByRaw as string[]) : [];
	// Without this flag R&A's summarized view hides non-numeric columns (utils.tsx:calculateIsColumnVisible).
	const showAllAggregationColumns = summarizeBy.length > 0;
	const columnsForMetadata = selectedColumns ?? [];
	const columnMeta: ColumnMeta = (reportConfig.columnMeta as ColumnMeta) ?? {};
	return {
		employeeQueryID: null,
		savedQuery: {
			type: reportConfig.type,
			name: reportConfig.name,
			description: reportConfig.description ?? "",
			tags: [],
			// Default to [] not null: R&A's resolver calls formatFilters(conditions).map(...),
			// which throws on null. A filterless AI report must match the UI save path's empty array.
			conditions: reportConfig.additional_filters ?? [],
		},
		selectedColumns,
		columnMetadata: reportConfig.columnMetadata ?? buildColumnMetadata(columnsForMetadata),
		quickFilter: Array.isArray(reportConfig.quickFilter) ? reportConfig.quickFilter : [],
		// MVP: the agent can only author fieldPath:[columnId], but the reporting query builds
		// the Mongo sort key directly from fieldPath (no columnId→path resolver client-side), so
		// agent sorts silently mis-sort. Ship un-sorted (R&A's default) until a resolver lands.
		sorts: [],
		summarizeBy,
		aggregationOperations: {},
		isSummarizeRowsView: reportConfig.isSummarizeRowsView ?? showAllAggregationColumns,
		showAllAggregationColumns,
		summaryColumnMetadata:
			reportConfig.summaryColumnMetadata ?? buildSummaryColumnMetadata(columnsForMetadata, columnMeta),
		summarizeByMetadata: reportConfig.summarizeByMetadata ?? buildSummarizeByMetadata(summarizeBy, columnMeta),
		visualizeOptions: buildVisualizeOptions(selectedChart, summarizeBy, columnsForMetadata, columnMeta),
		pageSize: 50,
		createdVia: "ai_interviewer",
	};
};

// Populating selectedQuestions is non-fatal UX-only: R&A renders question columns from
// uniqueReviewReportQuestionsMap regardless; this just preselects them in R&A's modal.
const buildQuestionSelections = (
	selectedColumns: unknown
): { _id: string; name: string; questions: string[]; is_grouped: boolean; summary_function: string }[] => {
	if (!Array.isArray(selectedColumns)) return [];
	const hashes = selectedColumns
		.filter((id): id is string => typeof id === "string" && id.startsWith("question_"))
		.map((id) => id.replace("question_", ""));
	return hashes.map((hash) => ({
		_id: new ObjectId().toString(),
		name: hash,
		questions: [hash],
		is_grouped: false,
		summary_function: "average",
	}));
};

export const useReportSave = () => {
	const [isSaving, setIsSaving] = useState(false);
	const [saveReportMutation] = useMutation(SAVE_REPORT_QUERY);
	const [setSelectedQuestionsMutation] = useMutation(SET_SELECTED_QUESTIONS);
	const setReportSaveStatus = useAskPerformYardStore((store) => store.setReportSaveStatus);
	const setSavedReportId = useAskPerformYardStore((store) => store.setSavedReportId);
	const reportBuilderStatus = useAskPerformYardStore((store) => store.reportBuilder?.status);

	const saveReport = useCallback(async (): Promise<void> => {
		const current = useAskPerformYardStore.getState().reportBuilder;
		if (!current?.reportConfig) return;
		if (current.status === "saving" || current.status === "saved") return;

		setIsSaving(true);
		setReportSaveStatus("saving");
		try {
			const variables = buildSaveVariables(current.reportConfig);
			const response = await saveReportMutation({ variables });
			const saved = response.data?.createSavedReviewReportQuery;
			const savedId = saved?.savedReviewReportQueryID;
			if (!savedId) {
				throw new Error("Save mutation returned no savedReviewReportQueryID");
			}
			const questionGroupingSessionID = saved?.questionGroupingSessionID;
			const questionSelections = buildQuestionSelections(current.reportConfig.selectedColumns);
			if (questionSelections.length > 0 && questionGroupingSessionID) {
				try {
					await setSelectedQuestionsMutation({
						variables: {
							questionGroupingSessionID,
							selectedQuestions: questionSelections,
						},
					});
				} catch (error) {
					// Non-fatal — the report rendered fine; only the modal's "selected" state lags.
					console.error("useReportSave setSelectedQuestions failed (non-fatal):", error);
				}
			}
			setSavedReportId(String(savedId));
			// This hook only runs for the report harness, and activeInterviewerHarness is
			// already null by save time (deactivated synchronously in useAskStream), so clear
			// the notes by the known key rather than reading the cleared store value.
			sessionStorage.removeItem(getInterviewerNotesKey("reporting_analytics_report"));
			setReportSaveStatus("saved");
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to save report. Please try again.";
			console.error("useReportSave save failed:", error);
			setReportSaveStatus("error", errorMessage);
		} finally {
			setIsSaving(false);
		}
	}, [saveReportMutation, setSelectedQuestionsMutation, setReportSaveStatus, setSavedReportId]);

	// Auto-fire on SSE-flipped "pending" status; failure leaves notes in sessionStorage for retry.
	useEffect(() => {
		if (reportBuilderStatus === "pending") {
			void saveReport();
		}
	}, [reportBuilderStatus, saveReport]);

	return { saveReport, isSaving };
};
